/**
 * The languages Elena talks in. Speech recognizers need the language up front (they can't
 * guess it), so the server keeps one "conversation language" that follows what you say and
 * every client listens in it.
 */
export const languages = ['sr', 'en', 'de'];
export const languageInfo = {
    sr: { label: 'Srpski', speech: 'sr-RS' },
    en: { label: 'English', speech: 'en-US' },
    de: { label: 'Deutsch', speech: 'de-DE' },
};
export const isLanguage = (value) => typeof value === 'string' && languages.includes(value);
/** Short, common words that give a language away. Names and loanwords are left out. */
const wordSet = (words) => new Set(words.split(/\s+/).filter(Boolean));
const markers = {
    sr: wordSet('je da i u na se mi ti me te ja on ona mi vi oni to ovo ono taj sta šta sto što kako gde gdje ' +
        'ko koji koja koje kad kada li ne nije jesam jesi jeste bih bi su sam smo ste od za sa iz po ' +
        'pusti pustis upali ugasi otvori zatvori pojacaj pojačaj smanji stavi daj reci kazi kaži ' +
        'hvala molim hajde ajde koliko kakvo kakav danas sutra juce juče sada sad malo vise više ' +
        'igraca igrača pesmu pesma muziku vreme podseti zapisi zapiši dobro jutro vece veče laku noc noć ' +
        'pricaj pričaj srpski srpskom moze može hocu hoću imam imas imaš treba'),
    en: wordSet('the a an is are was were be been am i you he she we they it its this that these those what ' +
        'who where when why how which do does did dont don can could would should will wont please ' +
        'play turn on off open close set put tell show give me my your our to of and or but with for ' +
        'from at by about some any song music weather today tomorrow now there here hello hi thanks ' +
        'thank yes no not speak english talk let lets is whats'),
    de: wordSet('der die das den dem des ein eine einen einem ist sind war bin bist ich du er sie wir ihr es ' +
        'nicht kein keine bitte danke ja nein wie was wer wo wann warum welche mach mache spiel spiele ' +
        'spielen schalte schalt an aus öffne offne zeig sag gib mir mich dich dir mein meine dein und ' +
        'oder aber mit für fur von zu auf im ins heute morgen jetzt hier dort wetter lied musik hallo ' +
        'sprich sprechen deutsch kannst kann möchte mochte ist es gibt'),
};
/**
 * The language of a message, or null when it is too short or mixed to tell ("Ceca", "ok").
 * Serbian letters (č ć š ž đ, Cyrillic) and German ones (ä ö ü ß) decide on their own.
 */
export function detectLanguage(text) {
    const lower = text.toLowerCase();
    if (/[а-яђјљњћџ]/.test(lower) || /[čćžđ]/.test(lower))
        return 'sr';
    if (/[äöüß]/.test(lower))
        return 'de';
    const words = lower.match(/[a-zšđčćž']+/g) ?? [];
    const score = { sr: 0, en: 0, de: 0 };
    for (const raw of words) {
        const w = raw.replace(/'/g, '');
        for (const lang of languages)
            if (markers[lang].has(w))
                score[lang]++;
    }
    const ranked = [...languages].sort((a, b) => score[b] - score[a]);
    const [best, second] = ranked;
    if (score[best] === 0 || score[best] === score[second])
        return null;
    return best;
}
const latinOf = {
    а: 'a',
    б: 'b',
    в: 'v',
    г: 'g',
    д: 'd',
    ђ: 'đ',
    е: 'e',
    ж: 'ž',
    з: 'z',
    и: 'i',
    ј: 'j',
    к: 'k',
    л: 'l',
    љ: 'lj',
    м: 'm',
    н: 'n',
    њ: 'nj',
    о: 'o',
    п: 'p',
    р: 'r',
    с: 's',
    т: 't',
    ћ: 'ć',
    у: 'u',
    ф: 'f',
    х: 'h',
    ц: 'c',
    ч: 'č',
    џ: 'dž',
    ш: 'š',
};
/** Serbian Cyrillic to Latin script, keeping capitals ("Љиљана" → "Ljiljana"); other text as is. */
export function toLatin(text) {
    return text.replace(/[Ѐ-ӿ]/g, (ch, i) => {
        const lower = ch.toLowerCase();
        const lat = latinOf[lower];
        if (!lat)
            return ch;
        if (ch === lower)
            return lat;
        // a capital digraph is "Lj" before lowercase, "LJ" in an all-caps word
        const next = text[i + 1];
        const allCaps = next !== undefined && next !== next.toLowerCase();
        return allCaps ? lat.toUpperCase() : lat[0].toUpperCase() + lat.slice(1);
    });
}
//# sourceMappingURL=language.js.map